import { AnimatePresence, motion } from "framer-motion";
import jsPDF from "jspdf";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CancelOrderDialog from "../components/CancelOrderDialog";
import OrderDetailsModal from "../components/OrderDetailsModal";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import { formatDeliveryDate, getEstimatedDeliveryDate } from "../utils/orderUtils";

const MyOrders = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderDetails, setShowOrderDetails] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [orderToCancel, setOrderToCancel] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState("");

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiService.getUserOrders(currentPage, 10, statusFilter);

            if (response.status === "success") {
                setOrders(response.data.orders || response.data);
                setTotalPages(response.pagination?.pages || 1);
            } else {
                setError("Failed to fetch orders");
            }
        } catch (err) {
            console.error("Error fetching orders:", err);
            setError("Failed to load orders. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, statusFilter]);

    const [searchParams, setSearchParams] = useSearchParams();
    const [paymentFeedback, setPaymentFeedback] = useState(null);

    useEffect(() => {
        if (isAuthenticated) {
            fetchOrders();
        }
    }, [isAuthenticated, fetchOrders]);

    useEffect(() => {
        const payment = searchParams.get("payment");
        const orderId = searchParams.get("orderId");
        if (payment) {
            if (payment === "success") {
                setPaymentFeedback({
                    type: "success",
                    title: "Payment Successful!",
                    message: "Your payment has been processed and your order is confirmed.",
                    orderId
                });
            } else if (payment === "failed") {
                setPaymentFeedback({
                    type: "error",
                    title: "Payment Failed",
                    message: "Your payment could not be completed. You can retry paying for your order below.",
                    orderId
                });
            } else if (payment === "cancelled") {
                setPaymentFeedback({
                    type: "warning",
                    title: "Payment Cancelled",
                    message: "You cancelled the payment transaction. Your pending order is saved.",
                    orderId
                });
            }
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleViewOrder = async orderId => {
        try {
            const response = await apiService.getOrderById(orderId);
            if (response.status === "success") {
                setSelectedOrder(response.data.order);
                setShowOrderDetails(true);
            }
        } catch (err) {
            console.error("Error fetching order details:", err);
        }
    };

    const handleCancelOrder = order => {
        setOrderToCancel(order);
        setShowCancelDialog(true);
    };

    const confirmCancelOrder = async reason => {
        try {
            const response = await apiService.cancelOrder(orderToCancel._id, reason);
            if (response.status === "success") {
                // Refresh orders list
                fetchOrders();
                setShowCancelDialog(false);
                setOrderToCancel(null);
            }
        } catch (err) {
            console.error("Error cancelling order:", err);
        }
    };

    const getStatusColor = status => {
        const colors = {
            pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
            confirmed: "bg-blue-100 text-blue-800 border-blue-200",
            designing: "bg-purple-100 text-purple-800 border-purple-200",
            "proof-sent": "bg-indigo-100 text-indigo-800 border-indigo-200",
            "proof-approved": "bg-green-100 text-[#008000] border-green-200",
            printing: "bg-orange-100 text-orange-800 border-orange-200",
            "quality-check": "bg-teal-100 text-teal-800 border-teal-200",
            packed: "bg-cyan-100 text-cyan-800 border-cyan-200",
            shipped: "bg-blue-100 text-blue-800 border-blue-200",
            delivered: "bg-green-100 text-[#008000] border-green-200",
            cancelled: "bg-red-100 text-red-800 border-red-200",
            returned: "bg-gray-100 text-gray-800 border-gray-200",
        };
        return colors[status] || "bg-gray-100 text-gray-800 border-gray-200";
    };

    const canCancelOrder = status => {
        return ["pending", "confirmed", "designing"].includes(status);
    };

    const isPaymentFailed = order => {
        return order.payment?.status === "failed";
    };

    const reconstructOrderData = order => {
        if (!order.items || order.items.length === 0) {
            return null;
        }

        const firstItem = order.items[0];
        const product = firstItem.product;

        let designData = null;
        if (firstItem.designUpload) {
            if (Array.isArray(firstItem.designUpload)) {
                designData = firstItem.designUpload.map(design => ({
                    cloudinaryId: design.public_id || design.id,
                    url: design.url,
                    originalName: design.originalName || design.name,
                    uploadedAt: design.uploadedAt || new Date().toISOString()
                }));
            } else {
                designData = {
                    cloudinaryId: firstItem.designUpload.public_id || firstItem.designUpload.id,
                    url: firstItem.designUpload.url,
                    originalName: firstItem.designUpload.originalName || firstItem.designUpload.name,
                    uploadedAt: firstItem.designUpload.uploadedAt || new Date().toISOString()
                };
            }
        }

        const customizations = firstItem.customizations || {};
        const totalAmount = order.pricing?.subtotal || order.totalAmount || 0;

        return {
            productId: product?._id || product?.id,
            product: product,
            selectedQuantity: firstItem.quantity,
            selectedFinish: customizations.finish || "matte",
            selectedCorners: customizations.corners || "rounded",
            selectedPaperType: customizations.paperType || null,
            selectedSize: customizations.size || null,
            designData: designData,
            totalAmount: totalAmount
        };
    };

    const handleTryAgain = order => {
        const orderData = reconstructOrderData(order);
        if (orderData) {
            navigate("/checkout", { state: { orderData } });
        } else {
            alert("Unable to retry payment. Please create a new order.");
        }
    };

    const formatDateForPDF = date => {
        return new Date(date).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatCurrencyForPDF = amount => {
        return `Rs. ${new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount)}`;
    };

    const generatePDF = order => {
        if (!order) return;

        const doc = new jsPDF();
        let yPos = 10;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 10;

        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("ORDER INVOICE", pageWidth / 2, yPos, { align: "center" });
        yPos += 6;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("Kigo Prints", pageWidth / 2, yPos, { align: "center" });
        yPos += 8;

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 6;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Order Information", margin, yPos);
        yPos += 5;

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(`Order Number: ${order.orderNumber || order._id}`, margin, yPos);
        doc.text(`Order Date: ${formatDateForPDF(order.createdAt)}`, margin, yPos + 4);
        doc.text(`Status: ${order.status.replace("-", " ").toUpperCase()}`, margin + 90, yPos);
        if (order.isRush) {
            doc.text("Rush Order: Yes", margin + 90, yPos + 4);
        }
        yPos += 10;

        if (order.shippingAddress) {
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            doc.text("Shipping Address", margin, yPos);
            yPos += 5;

            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            if (order.deliveryType === 'self-pickup' || order.shippingAddress.addressLine1 === 'Pick from store') {
                doc.text("Pick from store", margin, yPos);
                yPos += 6;
            } else {
                const fullName =
                    order.shippingAddress.fullName ||
                    `${order.shippingAddress.firstName || ""} ${order.shippingAddress.lastName || ""}`.trim();
                doc.text(fullName || "N/A", margin, yPos);
                yPos += 4;
                doc.text(`${order.shippingAddress.addressLine1 || order.shippingAddress.street || ""}`, margin, yPos);
                yPos += 4;
                if (order.shippingAddress.addressLine2) {
                    doc.text(`${order.shippingAddress.addressLine2}`, margin, yPos);
                    yPos += 4;
                }
                doc.text(
                    `${order.shippingAddress.city || ""}, ${order.shippingAddress.state || ""} ${order.shippingAddress.pincode || order.shippingAddress.postalCode || ""}`,
                    margin,
                    yPos
                );
                yPos += 4;
                if (order.shippingAddress.phone) {
                    doc.text(`Phone: ${order.shippingAddress.phone}`, margin, yPos);
                    yPos += 4;
                }
                yPos += 6;
            }
        }

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("Order Items", margin, yPos);
        yPos += 5;

        if (order.items && order.items.length > 0) {
            doc.setFontSize(7);
            doc.setFont("helvetica", "bold");
            doc.text("Product", margin, yPos);
            doc.text("Qty", margin + 80, yPos);
            doc.text("Unit Price", margin + 100, yPos);
            doc.text("Total", margin + 150, yPos);
            yPos += 4;

            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.5);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 4;

            doc.setFontSize(7);
            doc.setFont("helvetica", "normal");
            order.items.forEach(item => {
                if (yPos > pageHeight - 50) {
                    return;
                }

                const productName = item.product?.name || "Product Name";
                const quantity = item.quantity || 0;
                const unitPrice = item.unitPrice || 0;
                const totalPrice = item.totalPrice || 0;

                const lines = doc.splitTextToSize(productName, 70);
                doc.text(lines[0], margin, yPos);
                if (lines.length > 1) {
                    doc.text(lines[1], margin, yPos + 3);
                }

                doc.text(quantity.toString(), margin + 80, yPos);
                doc.text(formatCurrencyForPDF(unitPrice), margin + 100, yPos);
                doc.setFont("helvetica", "bold");
                doc.text(formatCurrencyForPDF(totalPrice), margin + 150, yPos);
                doc.setFont("helvetica", "normal");

                yPos += lines.length > 1 ? 7 : 5;
            });
        } else {
            doc.setFontSize(8);
            doc.text("No items found", margin, yPos);
            yPos += 5;
        }

        yPos += 5;

        const pricingStartX = margin + 100;
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("Pricing Details", pricingStartX, yPos);
        yPos += 5;

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        const subtotal = order.pricing?.subtotal || order.totalAmount || 0;
        doc.text("Subtotal:", pricingStartX, yPos);
        doc.setFont("helvetica", "bold");
        doc.text(formatCurrencyForPDF(subtotal), pageWidth - margin, yPos, { align: "right" });
        yPos += 4;

        if (order.pricing?.discount?.amount > 0) {
            doc.setFont("helvetica", "normal");
            doc.text("Discount:", pricingStartX, yPos);
            doc.setFont("helvetica", "bold");
            doc.text(`-${formatCurrencyForPDF(order.pricing.discount.amount)}`, pageWidth - margin, yPos, {
                align: "right",
            });
            yPos += 4;
        }

        if (order.pricing?.tax?.amount > 0) {
            doc.setFont("helvetica", "normal");
            doc.text("Tax:", pricingStartX, yPos);
            doc.setFont("helvetica", "bold");
            doc.text(formatCurrencyForPDF(order.pricing.tax.amount), pageWidth - margin, yPos, { align: "right" });
            yPos += 4;
        }

        if (order.pricing?.shipping?.cost > 0) {
            doc.setFont("helvetica", "normal");
            doc.text("Shipping:", pricingStartX, yPos);
            doc.setFont("helvetica", "bold");
            doc.text(formatCurrencyForPDF(order.pricing.shipping.cost), pageWidth - margin, yPos, { align: "right" });
            yPos += 4;
        }

        if (order.rushCharges > 0) {
            doc.setFont("helvetica", "normal");
            doc.text("Rush Charges:", pricingStartX, yPos);
            doc.setFont("helvetica", "bold");
            doc.text(formatCurrencyForPDF(order.rushCharges), pageWidth - margin, yPos, { align: "right" });
            yPos += 4;
        }

        yPos += 2;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(pricingStartX, yPos, pageWidth - margin, yPos);
        yPos += 4;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        const total = (order.pricing?.total || order.totalAmount || 0) + (order.rushCharges || 0);
        doc.text("Total:", pricingStartX, yPos);
        doc.setFontSize(10);
        doc.text(formatCurrencyForPDF(total), pageWidth - margin, yPos, { align: "right" });
        yPos += 8;

        if (order.payment && yPos < pageHeight - 30) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text("Payment Information", margin, yPos);
            yPos += 5;

            doc.setFontSize(7);
            doc.setFont("helvetica", "normal");
            doc.text(`Payment Method: ${order.payment.method || "N/A"}`, margin, yPos);
            doc.text(`Payment Status: ${order.payment.status?.toUpperCase() || "N/A"}`, margin + 90, yPos);
            yPos += 4;
            if (order.payment.ccavenueTrackingId) {
                doc.text(`CCAvenue Transaction ID: ${order.payment.ccavenueTrackingId}`, margin, yPos);
                yPos += 4;
            } else if (order.payment.razorpayPaymentId) {
                doc.text(`Razorpay Payment ID: ${order.payment.razorpayPaymentId}`, margin, yPos);
                yPos += 4;
            }
            yPos += 3;
        }

        if (order.specialInstructions && yPos < pageHeight - 20) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text("Special Instructions", margin, yPos);
            yPos += 5;

            const instructions = doc.splitTextToSize(order.specialInstructions, pageWidth - margin * 2);
            doc.setFontSize(7);
            doc.setFont("helvetica", "normal");
            instructions.forEach(line => {
                if (yPos < pageHeight - 10) {
                    doc.text(line, margin, yPos);
                    yPos += 4;
                }
            });
        }

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(`Generated on ${new Date().toLocaleDateString("en-IN")}`, pageWidth / 2, pageHeight - 8, {
            align: "center",
        });

        const fileName = `Order-${order.orderNumber || order._id}-${new Date().toISOString().split("T")[0]}.pdf`;
        doc.save(fileName);
    };

    const formatDate = dateString => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="text-center">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Please Sign In</h2>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">You need to be signed in to view your orders.</p>
                    <a
                        href="/signin"
                        className="inline-block bg-[#008000] text-white px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base rounded-lg hover:bg-[#008000] transition-colors"
                    >
                        Sign In
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-4 sm:py-8 sm:pt-32 pt-20">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-4 sm:mb-8"
                >
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">My Orders</h1>
                    <p className="text-xs sm:text-sm text-gray-600">Track and manage your orders</p>
                </motion.div>

                {paymentFeedback && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mb-4 sm:mb-6 p-4 rounded-xl border flex items-start justify-between ${
                            paymentFeedback.type === 'success'
                                ? 'bg-green-50 border-green-200 text-green-800'
                                : paymentFeedback.type === 'error'
                                ? 'bg-red-50 border-red-200 text-red-800'
                                : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                        }`}
                    >
                        <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-0.5">
                                {paymentFeedback.type === 'success' ? (
                                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold">{paymentFeedback.title}</h3>
                                <p className="text-xs mt-0.5">{paymentFeedback.message}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setPaymentFeedback(null)}
                            className="text-gray-400 hover:text-gray-600 ml-4 font-bold text-sm"
                        >
                            ✕
                        </button>
                    </motion.div>
                )}

                {/* Filters */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="mb-4 sm:mb-6"
                >
                    <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <div className="flex-1">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Filter by Status</label>
                                <div className="relative">
                                    <select
                                        value={statusFilter}
                                        onChange={e => setStatusFilter(e.target.value)}
                                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-xl text-xs sm:text-sm text-black focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white pr-8 sm:pr-10"
                                    >
                                        <option value="">All Orders</option>
                                        <option value="pending">Pending</option>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="designing">Designing</option>
                                        <option value="proof-sent">Proof Sent</option>
                                        <option value="printing">Printing</option>
                                        <option value="shipped">Shipped</option>
                                        <option value="delivered">Delivered</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 sm:pr-3">
                                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Orders List */}
                {loading ? (
                    <div className="flex items-center justify-center py-8 sm:py-12">
                        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-[#008000]"></div>
                    </div>
                ) : error ? (
                    <div className="text-center py-8 sm:py-12">
                        <div className="text-sm sm:text-base text-red-600 mb-3 sm:mb-4">{error}</div>
                        <button
                            onClick={fetchOrders}
                            className="bg-[#008000] text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg hover:bg-[#008000] transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                ) : orders.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="text-center py-8 sm:py-12"
                    >
                        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                <svg
                                    className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">No orders found</h3>
                            <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                                {statusFilter
                                    ? "No orders found with the selected status."
                                    : "You haven't placed any orders yet."}
                            </p>
                            {!statusFilter && (
                                <a
                                    href="/"
                                    className="inline-block bg-[#008000] text-white px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm rounded-lg hover:bg-[#008000] transition-colors"
                                >
                                    Start Shopping
                                </a>
                            )}
                        </div>
                    </motion.div>
                ) : (
                    <div className="space-y-3 sm:space-y-4">
                        <AnimatePresence>
                            {orders.map((order, index) => (
                                <motion.div
                                    key={order._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.4, delay: index * 0.05 }}
                                    className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                                >
                                    <div className="p-3 sm:p-4 lg:p-6">
                                        {/* Order Header */}
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-3 sm:mb-0">
                                                <div>
                                                    <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
                                                        Order #{order.orderNumber || order._id.slice(-8)}
                                                    </h3>
                                                    <p className="text-xs sm:text-sm text-gray-600">
                                                        Placed on {formatDate(order.createdAt)}
                                                    </p>
                                                </div>
                                                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                    <span
                                                        className={`inline-flex px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full border ${getStatusColor(
                                                            order.status
                                                        )}`}
                                                    >
                                                        {order.status.charAt(0).toUpperCase() +
                                                            order.status.slice(1).replace("-", " ")}
                                                    </span>
                                                    {isPaymentFailed(order) && (
                                                        <span className="inline-flex px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full border bg-red-100 text-red-800 border-red-200">
                                                            Payment Failed
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-left sm:text-right">
                                                <div className="text-base sm:text-lg font-bold text-[#008000]">
                                                    ₹{order.pricing?.total || order.totalAmount || 0}
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-500">
                                                    {order.items?.length || 0} item
                                                    {(order.items?.length || 0) !== 1 ? "s" : ""}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Order Items Preview */}
                                        {order.items && order.items.length > 0 && (
                                            <div className="mb-3 sm:mb-4">
                                                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2">
                                                    <span>Items:</span>
                                                </div>
                                                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                    {order.items.slice(0, 3).map((item, itemIndex) => (
                                                        <span
                                                            key={itemIndex}
                                                            className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 text-gray-700 text-[10px] sm:text-xs rounded"
                                                        >
                                                            {item.product?.name || "Product"} × {item.quantity}
                                                        </span>
                                                    ))}
                                                    {order.items.length > 3 && (
                                                        <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 text-gray-700 text-[10px] sm:text-xs rounded">
                                                            +{order.items.length - 3} more
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Estimated Delivery Date */}
                                        <div className="mb-3 sm:mb-4 pt-3 border-t border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs sm:text-sm text-gray-600">Estimated Delivery:</span>
                                                <span className="text-xs sm:text-sm font-medium text-gray-900">
                                                    {formatDeliveryDate(getEstimatedDeliveryDate(order))}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                            <button
                                                onClick={() => handleViewOrder(order._id)}
                                                className="flex-1 bg-[#008000] text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl hover:bg-[#008000] transition-colors text-center"
                                            >
                                                View Details
                                            </button>
                                            {order.status !== "pending" && order.status !== "cancelled" && (
                                                <button
                                                    onClick={() => generatePDF(order)}
                                                    className="bg-indigo-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5"
                                                >
                                                    <svg
                                                        className="w-3 h-3 sm:w-4 sm:h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                        />
                                                    </svg>
                                                    <span className="hidden sm:inline">Invoice</span>
                                                </button>
                                            )}
                                            {isPaymentFailed(order) ? (
                                                <button
                                                    onClick={() => handleTryAgain(order)}
                                                    className="bg-blue-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl hover:bg-blue-700 transition-colors"
                                                >
                                                    Try Again
                                                </button>
                                            ) : canCancelOrder(order.status) ? (
                                                <button
                                                    onClick={() => handleCancelOrder(order)}
                                                    className="bg-red-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl hover:bg-red-700 transition-colors"
                                                >
                                                    Cancel Order
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="mt-4 sm:mt-8 flex justify-center"
                    >
                        <div className="flex space-x-1 sm:space-x-2">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                const pageNum = i + 1;
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg ${pageNum === currentPage
                                            ? "bg-[#008000] text-white"
                                            : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Order Details Modal */}
            {showOrderDetails && selectedOrder && (
                <OrderDetailsModal
                    order={selectedOrder}
                    onClose={() => {
                        setShowOrderDetails(false);
                        setSelectedOrder(null);
                    }}
                    onCancelOrder={canCancelOrder(selectedOrder.status) ? () => handleCancelOrder(selectedOrder) : null}
                    onTryAgain={isPaymentFailed(selectedOrder) ? () => handleTryAgain(selectedOrder) : null}
                />
            )}

            {/* Cancel Order Dialog */}
            {showCancelDialog && orderToCancel && (
                <CancelOrderDialog
                    order={orderToCancel}
                    onConfirm={confirmCancelOrder}
                    onCancel={() => {
                        setShowCancelDialog(false);
                        setOrderToCancel(null);
                    }}
                />
            )}
        </div>
    );
};

export default MyOrders;
